import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDescope, useSession } from '@descope/react-sdk';
import { useAlert } from 'context/AlertContext';
import { SignUpLayout, SignUpForm } from 'components/organisms';
import { SignUpFormData, AuthError } from 'types/auth.types';
import useAuthStore from 'stores/authStore';
import UserService from 'services/userService';
import { UserApiError, UserErrorType } from 'types/user.types';
import { analyticsService } from 'services/analyticsService';

interface UiMessage {
  kind: 'auth' | 'rate-limit' | 'policy' | 'validation' | 'network' | 'server' | 'unknown';
  text: string;
}

// Error extraction function (same as Login)
async function extractDescopeError(errOrResult: any): Promise<AuthError> {
  console.log('[SignUp] Extracting error from:', errOrResult);
  
  if (errOrResult instanceof Response) {
    let payload: any = {};
    try {
      payload = await errOrResult.clone().json();
      console.log('[SignUp] Response payload:', payload);
    } catch (_) {
      console.log('[SignUp] Could not parse response as JSON');
    }
    return {
      status: errOrResult.status,
      code: payload?.errorCode || payload?.code,
      desc: payload?.errorDescription || payload?.message || "Request failed",
    };
  }

  if (errOrResult && typeof errOrResult === "object") {
    const status = errOrResult.status || errOrResult.code;
    const code =
      (errOrResult.error && errOrResult.error.errorCode) ||
      errOrResult.errorCode ||
      errOrResult.code;
    const desc =
      (errOrResult.error && errOrResult.error.errorDescription) ||
      errOrResult.errorDescription ||
      errOrResult.message;
    
    console.log('[SignUp] Extracted from object:', { status, code, desc });
    
    if (status || code || desc) return { status, code, desc };
  }

  if (errOrResult instanceof TypeError) {
    console.log('[SignUp] Network error detected');
    return { status: undefined, code: "err_network", desc: errOrResult.message || "Network error" };
  }

  if (errOrResult && typeof errOrResult.message === "string") {
    console.log('[SignUp] Error with message:', errOrResult.message);
    return { status: undefined, code: "err_unknown", desc: errOrResult.message };
  }

  console.log('[SignUp] Unknown error type');
  return { status: undefined, code: "err_unknown", desc: "Unknown error" };
}

// UI message mapping function (same as Login, adapted for signup)
function mapToUiMessage({ status, code, desc }: AuthError): UiMessage {
  console.log('[SignUp] Mapping error to UI message:', { status, code, desc });
  
  const isEmailExists =
    status === 409 ||
    code === "err_email_exists" ||
    code === "err_user_exists" ||
    /already exists/i.test(desc || "") ||
    /user.*found/i.test(desc || "");

  if (isEmailExists) {
    return { kind: "auth", text: "An account with this email already exists. Try signing in instead." };
  }

  const isWeakPassword =
    code === "err_weak_password" ||
    code === "err_password_policy" ||
    /password.*weak/i.test(desc || "") ||
    /password.*requirements/i.test(desc || "");

  if (isWeakPassword) {
    return { kind: "validation", text: "Password does not meet requirements. Use at least 8 characters with letters and numbers." };
  }

  if (status === 429 || code === "err_too_many_attempts") {
    return { kind: "rate-limit", text: "Too many signup attempts. Please wait a minute and try again." };
  }

  if (
    status === 403 ||
    code === "err_domain_not_allowed" ||
    code === "err_signup_disabled" ||
    code === "err_registration_disabled"
  ) {
    return { kind: "policy", text: "Sign-up is currently restricted. Please contact support." };
  }

  if (code === "err_validation" || code === "err_invalid_arguments" || code === "err_invalid_email") {
    return { kind: "validation", text: "Please check your inputs and try again." };
  }

  if (code === "err_network") {
    return { kind: "network", text: "Cannot reach the server. Check your connection and retry." };
  }

  if (status && status >= 500) {
    return { kind: "server", text: "Authentication service is temporarily unavailable. Please try again shortly." };
  }

  return { kind: "unknown", text: desc || "Something went wrong during signup. Please try again." };
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const sdk = useDescope();
  const { isSessionLoading } = useSession();
  const { showSuccess, showError, showWarning, showInfo } = useAlert();
  const { setAuth } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSignUp = async (formData: SignUpFormData): Promise<void> => {
    console.log('[SignUp] Starting signup process...');
    console.log('[SignUp] Form data received:', {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      passwordLength: formData.password.length,
      confirmPasswordLength: formData.confirmPassword.length,
      passwordsMatch: formData.password === formData.confirmPassword
    });
    
    setLoading(true);
    setError('');
    
    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        console.log('[SignUp] Passwords do not match');
        throw new Error('PasswordMismatch');
      }
      
      // Warn about password length
      if (formData.password.length < 8) {
        console.log('[SignUp] Password is short but proceeding');
        showWarning('Password should be at least 8 characters long for better security.');
      }
      
      // Prepare user data for Descope
      const userData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        givenName: formData.firstName,
        familyName: formData.lastName,
        email: formData.email
      };
      
      console.log('[SignUp] Sending to Descope:');
      console.log('   - Email:', formData.email);
      console.log('   - Password length:', formData.password.length);
      console.log('   - User data:', JSON.stringify(userData, null, 2));
      console.log('   - SDK available:', !!sdk);
      console.log('   - SDK.password available:', !!sdk?.password);
      console.log('   - SDK.password.signUp available:', !!sdk?.password?.signUp);
      
      // Call Descope signup
      const result = await sdk.password.signUp(formData.email, formData.password, userData);
      
      console.log('[SignUp] Descope response received:', result);
      console.log('[SignUp] Response type:', typeof result);
      console.log('[SignUp] Response keys:', result ? Object.keys(result) : 'null');
      
      // Check if result indicates failure
      if (result && (result as any).ok === false) {
        console.log('[SignUp] Descope returned ok=false');
        const norm = await extractDescopeError(result);
        const ui = mapToUiMessage(norm);
        
        if (ui.kind === "rate-limit" || ui.kind === "validation" || ui.kind === "policy") {
          showWarning(ui.text);
        } else {
          showError(ui.text);
        }
        setError(ui.text);
        return;
      }
      
      console.log('[SignUp] Signup successful!');

      // Extract session token and user data from the result
      const jwtResponse = (result as any).data;
      console.log('[SignUp] JWT Response:', jwtResponse);

      if (jwtResponse) {
        // Store session token and user data in auth store
        if (jwtResponse.sessionJwt && jwtResponse.user) {
          const user = jwtResponse.user;

          // Store both token and user in Zustand store
          setAuth(
            {
              userId: user.userId,
              email: user.email,
              name: user.name,
              givenName: user.givenName,
              familyName: user.familyName,
              loginIds: user.loginIds,
              verifiedEmail: user.verifiedEmail,
              picture: user.picture,
              roleNames: user.roleNames,
            },
            jwtResponse.sessionJwt
          );

          console.log('[SignUp] Auth data stored in store:', {
            userId: user.userId,
            email: user.email,
            name: user.name,
          });

          // Track signup event in Mixpanel
          analyticsService.trackSignup('email', user.userId);
          console.log('✅ [SignUp] Signup event tracked in Mixpanel');

          // Create user in backend database
          try {
            console.log('[SignUp] Creating user in backend...');
            await UserService.createUser({
              user_id: user.userId,
              email: user.email,
              name: userData.name,
              given_name: user.givenName || formData.firstName,
              family_name: user.familyName || formData.lastName,
              picture: user.picture,
              is_active: true,
              is_verified: user.verifiedEmail || false,
            });
            console.log('[SignUp] User created in backend successfully');
          } catch (backendError: any) {
            console.error('[SignUp] Failed to create user in backend:', backendError);

            // Handle UserApiError with specific error types
            if (backendError instanceof UserApiError) {
              switch (backendError.type) {
                case UserErrorType.USER_ALREADY_EXISTS:
                  // User already exists in backend - this is okay, they can proceed
                  console.log('[SignUp] User already exists in backend, proceeding anyway');
                  break;

                case UserErrorType.NETWORK_ERROR:
                  // Network issue - warn but let them proceed
                  showWarning('Network error during profile sync. Your account is created, but some data may not be saved. Please check your profile later.');
                  break;

                case UserErrorType.SERVER_ERROR:
                  // Server error - warn but let them proceed
                  showWarning('Server error during profile sync. Your account is created, but please verify your profile information later.');
                  break;

                case UserErrorType.INVALID_DATA:
                  // Data validation error - this shouldn't happen but warn if it does
                  showWarning('Profile data validation error. Your account is created, but please update your profile information.');
                  break;

                default:
                  // Unknown error - generic warning
                  showWarning('Profile sync issue. Your account is created, but please verify your profile later.');
              }
            } else {
              // Non-UserApiError - generic handling
              showWarning('Account created, but profile sync encountered an issue. Please verify your profile information.');
            }

            // Don't throw - user is already created in Descope and can proceed
          }
        } else {
          console.warn('[SignUp] Missing session token or user data in response');
        }
      }

      showSuccess(`Welcome ${formData.firstName}! Your account has been created successfully.`);

      setTimeout(() => {
        console.log('[SignUp] Navigating to landing page...');
        navigate('/');
      }, 1500);
      
    } catch (thrown) {
      console.error('[SignUp] Error caught:', thrown);
      console.error('[SignUp] Error type:', typeof thrown);
      console.error('[SignUp] Error keys:', thrown ? Object.keys(thrown) : 'null');
      
      const norm = await extractDescopeError(thrown);
      const ui = mapToUiMessage(norm);
      
      console.log('[SignUp] Final error message:', ui.text);
      
      if (ui.kind === "rate-limit" || ui.kind === "validation" || ui.kind === "policy") {
        showWarning(ui.text);
      } else {
        showError(ui.text);
      }
      
      setError(ui.text);
      
    } finally {
      setLoading(false);
      console.log('[SignUp] Signup process completed');
    }
  };

  const handleGoogleSignUp = async (): Promise<void> => {
    console.log('[SignUp] Starting Google signup...');
    
    try {
      setLoading(true);
      setError('');
      
      console.log('[SignUp] Checking OAuth availability...');
      console.log('   - SDK available:', !!sdk);
      console.log('   - SDK.oauth available:', !!sdk?.oauth);
      
      if (!sdk.oauth) {
        console.log('[SignUp] OAuth not configured');
        throw new Error('OAuthNotConfigured');
      }
      
      console.log('[SignUp] Initiating Google OAuth...');
      console.log('   - Provider: google');
      console.log('   - Redirect URL:', window.location.href);
      
      await sdk.oauth.start('google', window.location.href);
      
      console.log('[SignUp] Google OAuth started successfully');
      showInfo('Redirecting to Google to create your account...');
      
    } catch (err) {
      console.error('[SignUp] Google signup error:', err);
      
      const norm = await extractDescopeError(err);
      const ui = mapToUiMessage(norm);
      
      console.log('[SignUp] Google error message:', ui.text);
      showError(ui.text);
      setError(ui.text);
      
    } finally {
      setLoading(false);
      console.log('[SignUp] Google signup process completed');
    }
  };

  const handleSignIn = (): void => {
    console.log('[SignUp] Navigating to login page');
    navigate('/login');
  };

  const handleFormChange = (): void => {
    if (error) {
      console.log('[SignUp] Clearing error state');
      setError('');
    }
  };


  console.log('[SignUp] Rendering SignUp component');
  console.log('   - Loading:', loading);
  console.log('   - Session loading:', isSessionLoading);
  console.log('   - Error:', error);

  return (
    <SignUpLayout>
      <SignUpForm
        onSignUp={handleSignUp}
        onGoogleSignUp={handleGoogleSignUp}
        onSignIn={handleSignIn}
        onFormChange={handleFormChange}
        loading={loading || isSessionLoading}
        error={error}
      />
    </SignUpLayout>
  );
};

export default SignUp;
